import { useMemo, useState, useRef, React, useEffect, useContext } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LogarithmicScale,
} from "chart.js";
import { Pie, Bar, getElementAtEvent } from "react-chartjs-2";
import LoadingOverlay from "react-loading-overlay";
import { Card, CardBody, Row, Col, CardTitle, CardFooter } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";
import { MainContext } from "context/MainContext.js";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
LoadingOverlay.propTypes = undefined;
import {
  faBullhorn,
  faServer,
  faInfoCircle,
  faEnvelope,
  faScroll,
  faCity,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";

function Dashboard() {
  ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    LogarithmicScale,
  );

  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);

  const [showLoading, setShowLoading] = useState(false);

  const [data, setData] = useState([]);

  const [alarmsCount, setAlarmsCount] = useState([]);
  const [alarmsLabels, setAlarmsLabels] = useState([]);

  const [softwareCount, setSoftwareCount] = useState([]);
  const [softwareLabels, setSoftwareLabels] = useState([]);

  const [subscriberPerBandwidthCount, setSubscriberPerBandwidthCount] =
    useState([]);
  const [subscriberPerBandwidthLabels, setSubscriberPerBandwidthLabels] =
    useState([]);

  useEffect(() => {
    if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const genericColors = [
    "rgba(79, 129, 189, 1)", // Moderate Blue
    //"rgba(89, 161, 24, 1)", // Desaturated Green
    //"rgba(232, 80, 77, 1)", // Muted Red
    //"rgba(237, 145, 49, 1)", // Soft Orange
    //"rgba(155, 187, 89, 1)", // Olive Green
    //"rgba(165, 165, 165, 1)", // Warm Gray
    //"rgba(149, 179, 215, 1)", // Light Steel Blue
    //"rgba(217, 150, 148, 1)", // Dusty Rose
  ];

  const endpointStatusColors = [
    "rgba(232, 80, 77, 1)", // Muted Red
    "rgba(237, 145, 49, 1)", // Soft Orange
    "rgba(240, 205, 102, 1)", // Soft Yellow
    "rgb(175, 175, 175)", // Light Grey
    "rgba(89, 161, 24, 1)", // Desaturated Green
  ];

  const gamStatusColors = [
    "rgba(89, 161, 24, 1)", // Desaturated Green
    "rgba(232, 80, 77, 1)", // Muted Red
  ];

  const gamBaselineColors = [
    "rgba(89, 161, 24, 1)", // Desaturated Green
    "rgba(79, 129, 189, 1)", // Moderate Blue
  ];

  const installationColors = [
    "rgba(89, 161, 24, 1)", // Desaturated Green
    "rgba(237, 145, 49, 1)", // Soft Orange
    "rgba(149, 179, 215, 1)", // Light Steel Blue
    "rgb(175, 175, 175)", // Light Grey
    "rgb(0, 0, 0)", // Black
  ];

  const activeAlarmsColor = [
    "rgba(232, 80, 77, 1)", // Muted Red
    "rgba(237, 145, 49, 1)", // Soft Orange
    "rgba(240, 205, 102, 1)", // Soft Yellow
    "rgba(79, 129, 189, 1)", // Moderate Blue
  ];

  const deviceStatusRef = useRef();
  const clickDeviceStatus = (event) => {
    const elements = getElementAtEvent(deviceStatusRef.current, event);
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      if (elementIndex == 0) {
        // Show Online devices
        history.push({
          pathname: "/admin/devices/list",
          state: { value: "status||online" },
        });
      } else if (elementIndex == 1) {
        // Show Offline devices
        history.push({
          pathname: "/admin/devices/list",
          state: { value: "status||offline" },
        });
      }
    }
  };

  const clickActiveCriticalAlarms = () => {
    history.push({
      pathname: "/admin/alarms/severity",
      state: { params: "severity||cr" },
    });
  };

  const clickRecentCriticalAlarms = () => {
    history.push({
      pathname: "/admin/alarms/severity",
      state: { params: "History24hCritical" },
    });
  };

  const activeAlarmsRef = useRef();
  const clickActiveAlarms = (event) => {
    const elements = getElementAtEvent(activeAlarmsRef.current, event);
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      if (elementIndex == 0) {
        // Show critical alarms
        history.push({
          pathname: "/admin/alarms/list",
          state: { params: "severity||cr" },
        });
      } else if (elementIndex == 1) {
        // Show major alarms
        history.push({
          pathname: "/admin/alarms/list",
          state: { params: "severity||mj" },
        });
      } else if (elementIndex == 2) {
        // Show minor alarms
        history.push({
          pathname: "/admin/alarms/list",
          state: { params: "severity||mn" },
        });
      } else if (elementIndex == 3) {
        // Show na alarms
        history.push({
          pathname: "/admin/alarms/list",
          state: { params: "severity||na" },
        });
      }
    }
  };

  const quarantinedRef = useRef();
  const clickQuarantined = (event) => {
    const elements = getElementAtEvent(quarantinedRef.current, event);
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      if (elementIndex == 0) {
        history.push({
          pathname: "/admin/endpoints/list",
          state: { value: "status||offline" },
        });
      } else if (elementIndex == 1) {
        history.push({
          pathname: "/admin/endpoints/list",
          state: { value: "status||mjtca" },
        });
      } else if (elementIndex == 2) {
        history.push({
          pathname: "/admin/endpoints/list",
          state: { value: "status||mntca" },
        });
      } else if (elementIndex == 3) {
        history.push({
          pathname: "/admin/endpoints/list",
          state: { value: "status||notconfigured" },
        });
      } else if (elementIndex == 4) {
        history.push({
          pathname: "/admin/endpoints/list",
          state: { value: "status||ok" },
        });
      }
    }
  };

  const getVMStatus = (level) => {
    const colors = {
      2: "rgba(89, 161, 24, 1)", // Green
      1: "rgba(240, 205, 102, 1)", // Yellow
      0: "rgba(232, 80, 77, 1)", // Red
    };
    return (
      <FontAwesomeIcon
        icon={faServer}
        style={{ color: colors[level] ?? colors[0], paddingTop: "10px" }}
        size="3x"
      />
    );
  };

  const getStatusText = (level) => {
    if (level === 2) return "Optimal";
    if (level === 1) return "Adequate";
    return "Critical";
  };

  const getNAStatusText = (level) => {
    if (level == 3) {
      return "Optimal";
    } else if (level == 2) {
      return "Fair";
    } else if (level == 1) {
      return "Error";
    } else if (level == 0) {
      return "N/A";
    }
  };

  const getNotificationStatus = (level) => {
    if (level == 3) {
      return (
        <FontAwesomeIcon
          icon={faEnvelope}
          style={{ color: "rgba(89, 161, 24, 1)", paddingTop: "10px" }} // Desaturated Green
          size="3x"
        />
      );
    } else if (level == 2) {
      return (
        <FontAwesomeIcon
          icon={faEnvelope}
          style={{ color: "rgba(240, 205, 102, 1)", paddingTop: "10px" }} // Soft Yellow
          size="3x"
        />
      );
    } else if (level == 1) {
      return (
        <FontAwesomeIcon
          icon={faEnvelope}
          style={{ color: "rgba(232, 80, 77, 1)", paddingTop: "10px" }} // Muted Red
          size="3x"
        />
      );
    } else if (level == 0) {
      return (
        <FontAwesomeIcon
          icon={faEnvelope}
          style={{ color: "rgba(165, 165, 165, 1)", paddingTop: "10px" }} // Warm Gray
          size="3x"
        />
      );
    }
  };

  const getActiveCritialAlarmsColor = () => {
    let color = "rgb(175, 175, 175)";

    if (data && data.activeCriticalAlarms > 0) {
      color = "rgba(232, 80, 77, 1)";
    }

    return color;
  };

  const getRecentCritialAlarmsColor = () => {
    let color = "rgb(175, 175, 175)";

    if (data && data.recentCriticalAlarms24H > 0) {
      color = "rgba(232, 80, 77, 1)";
    }

    return color;
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: true,
        labels: {
          font: {
            size: 9,
          },
        },
      },
      title: {
        display: false,
      },
    },
    animation: {
      duration: 0,
    },
  };

  const getVMStatusTooltip = (vmSettingsMsg) => {
    if (vmSettingsMsg) {
      return (
        <>
          <div
            style={{
              color: getVmStatusTooltipColor(vmSettingsMsg.vm_disk_space),
            }}
          >
            Disk Space: &nbsp;{vmSettingsMsg.vm_disk_space.split("||")[1]}
          </div>
          <div
            style={{ color: getVmStatusTooltipColor(vmSettingsMsg.vm_memory) }}
          >
            Memory: &nbsp;{vmSettingsMsg.vm_memory.split("||")[1]}
          </div>
          <div
            style={{ color: getVmStatusTooltipColor(vmSettingsMsg.vm_core) }}
          >
            CPU Cores: &nbsp;{vmSettingsMsg.vm_core.split("||")[1]}
          </div>
          <div style={{ color: getVmStatusTooltipColor(vmSettingsMsg.vm_os) }}>
            OS: &nbsp;{vmSettingsMsg.vm_os.split("||")[1]}
          </div>
        </>
      );
    } else {
      return "";
    }
  };

  const getVmStatusTooltipColor = (value) => {
    if (value.startsWith("OK")) {
      return "lightgreen";
    } else if (value.startsWith("WARN")) {
      return "orange";
    } else {
      return "red";
    }
  };

  const getNotificationStatusTooltip = (notificationsMsg) => {
    if (notificationsMsg) {
      return (
        <>
          <div
            style={{
              color: notificationsMsg.sent >= 0 ? "lightgreen" : "red",
            }}
          >
            Sent: &nbsp; {notificationsMsg.sent}
          </div>
          <div
            style={{
              color: notificationsMsg.failed > 0 ? "red" : "lightgreen",
            }}
          >
            Failed: &nbsp; {notificationsMsg.failed}
          </div>
        </>
      );
    } else {
      return "";
    }
  };

  const redirectToNotifications = () => {
    if (Utilities.hasAccess(privileges["notifications.display"])) {
      const location = {
        pathname: "/admin/notification/list",
      };
      history.push(location);
    }
  };

  useEffect(() => {
    setShowLoading(true);
    fetchData("manual");

    const intervalIDValue = setInterval(() => {
      fetchData("auto");
    }, 15000);

    return () => {
      clearInterval(intervalIDValue);
    };
  }, []);

  const getNotificationCursor = () => {
    if (Utilities.hasAccess(privileges["notifications.display"])) {
      return "pointer";
    } else {
      return "default";
    }
  };

  const fetchData = (mode) => {
    const start = Date.now();
    fetch("/api/v1/dashboard/charts", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        mode: mode,
        sessionId: sessionStorage.getItem("sessionId"),
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        // Reset the alarms info
        setAlarmsCount([]);
        setAlarmsLabels([]);

        // Loop on the result to format the alarms info
        actualData.data.alarmsDisplay.map((item) => {
          setAlarmsCount((alarmsCount) => [...alarmsCount, item.count]);
          setAlarmsLabels((alarmsLabels) => [
            ...alarmsLabels,
            item.severity.toUpperCase(),
          ]);
        });

        // Reset the software info
        setSoftwareCount([]);
        setSoftwareLabels([]);

        // Loop on the result to format the software info
        actualData.data.softwareVersions.map((item) => {
          setSoftwareCount((softwareCount) => [...softwareCount, item.count]);
          setSoftwareLabels((softwareLabels) => [
            ...softwareLabels,
            item.softwareVersion
              ? item.softwareVersion.toUpperCase().substring(0, 18)
              : "",
          ]);
        });

        // Reset the subscriber per bandwidth info
        setSubscriberPerBandwidthCount([]);
        setSubscriberPerBandwidthLabels([]);

        // Loop on the result to format the alarms info
        actualData.data.subscribersBandwidths.map((item) => {
          setSubscriberPerBandwidthCount((subscriberPerBandwidthCount) => [
            ...subscriberPerBandwidthCount,
            item.count,
          ]);
          setSubscriberPerBandwidthLabels((subscriberPerBandwidthLabels) => [
            ...subscriberPerBandwidthLabels,
            item.bandwidth != ""
              ? item.bandwidth.toUpperCase().substring(0, 20)
              : "UNTHROTTLED",
          ]);
        });

        if (actualData.status == "200") {
          setData(actualData.data);
        }

        setShowLoading(false);
      })
      .catch((err) => {
        console.log(err.message);
        setShowLoading(false);
      });
  };

  return (
    <>
      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading..."
          className={showLoading ? "loading" : ""}
        />
        <Row>
          <Col>
            <Card
              className="card-stats"
              onClick={clickActiveCriticalAlarms}
              style={{ cursor: "pointer" }}
            >
              <CardBody>
                <Row>
                  <Col md="4" xs="5">
                    <div style={{ paddingLeft: "0.5em" }}>
                      <FontAwesomeIcon
                        icon={faBullhorn}
                        size="3x"
                        style={{
                          color: getActiveCritialAlarmsColor(),
                          paddingTop: "10px",
                        }}
                      />
                    </div>
                  </Col>
                  <Col md="8" xs="7">
                    <div className="numbers">
                      <p className="card-category" style={{ fontSize: "14px" }}>
                        Active Critical Alarms
                      </p>
                      <CardTitle tag="p">
                        <span style={{ fontSize: "0.8em" }}>
                          {data.activeCriticalAlarms}
                        </span>
                      </CardTitle>
                      <p />
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <i className="fa fa-refresh" />
                  Current Status
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card
              className="card-stats"
              onClick={clickRecentCriticalAlarms}
              style={{ cursor: "pointer" }}
            >
              <CardBody>
                <Row>
                  <Col md="4" xs="5">
                    <div style={{ paddingLeft: "0.5em" }}>
                      <FontAwesomeIcon
                        icon={faScroll}
                        size="3x"
                        style={{
                          color: getRecentCritialAlarmsColor(),
                          paddingTop: "10px",
                        }}
                      />
                    </div>
                  </Col>
                  <Col md="8" xs="7">
                    <div className="numbers">
                      <p className="card-category" style={{ fontSize: "14px" }}>
                        Recent Critical Alarms
                      </p>
                      <CardTitle tag="p">
                        <span style={{ fontSize: "0.8em" }}>
                          {data.recentCriticalAlarms24H}
                        </span>
                      </CardTitle>
                      <p />
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <i className="fa fa-refresh" />
                  Last 24H
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col md="4" xs="5">
                    <div style={{ paddingLeft: "0.5em" }}>
                      {getVMStatus(data.vmSettingsLevel)}
                    </div>
                  </Col>
                  <Col md="8" xs="7">
                    <div className="numbers">
                      <p className="card-category" style={{ fontSize: "14px" }}>
                        VM
                      </p>
                      <CardTitle tag="p">
                        <span style={{ fontSize: "0.8em" }}>
                          {getStatusText(data.vmSettingsLevel)}
                        </span>
                      </CardTitle>
                      <p />
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <Row>
                  <Col md="10">
                    <div className="stats">
                      <i className="fa fa-refresh" />
                      Current Status
                    </div>
                  </Col>
                  <Col md="2" style={{ textAlign: "right" }}>
                    <a className="vmElement">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </a>
                    <ReactTooltip
                      anchorSelect=".vmElement"
                      place="top"
                      style={{ textAlign: "left", zIndex: "100" }}
                    >
                      {getVMStatusTooltip(data.vmSettingsMsg)}
                    </ReactTooltip>
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card
              className="card-stats"
              onClick={redirectToNotifications}
              style={{ cursor: getNotificationCursor() }}
            >
              <CardBody>
                <Row>
                  <Col md="4" xs="5">
                    <div style={{ paddingLeft: "0.5em" }}>
                      {getNotificationStatus(data.notificationLevel)}
                    </div>
                  </Col>
                  <Col md="8" xs="7">
                    <div className="numbers">
                      <p className="card-category" style={{ fontSize: "14px" }}>
                        Notification
                      </p>
                      <CardTitle tag="p">
                        <span style={{ fontSize: "0.8em" }}>
                          {getNAStatusText(data.notificationLevel)}
                        </span>
                      </CardTitle>
                      <p />
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <Row>
                  <Col md="10">
                    <div className="stats">
                      <i className="fa fa-refresh" />
                      Last 24H
                    </div>
                  </Col>
                  <Col md="2" style={{ textAlign: "right" }}>
                    <a className="notificationElement">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </a>
                    <ReactTooltip
                      anchorSelect=".notificationElement"
                      place="top"
                      style={{ textAlign: "left", zIndex: "100" }}
                    >
                      {getNotificationStatusTooltip(data.notificationsMsg)}
                    </ReactTooltip>
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </Col>
          {data.installationDisplay ? (
            <Col>
              <Card className="card-stats">
                <CardBody>
                  <Row>
                    <Col md="4" xs="5">
                      <div style={{ paddingLeft: "0.5em" }}>
                        <FontAwesomeIcon
                          icon={faCity}
                          size="3x"
                          style={{
                            color: "rgba(89, 161, 24, 1)",
                            paddingTop: "10px",
                          }}
                        />
                      </div>
                    </Col>
                    <Col md="8" xs="7">
                      <div className="numbers">
                        <p
                          className="card-category"
                          style={{ fontSize: "14px" }}
                        >
                          Installations Completed Today
                        </p>
                        <CardTitle tag="p">
                          <span style={{ fontSize: "0.8em" }}>
                            {data.installationLast24h}
                          </span>
                        </CardTitle>
                        <p />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter>
                  <hr />
                  <div className="stats">
                    <i className="fa fa-refresh" />
                    Last 24H
                  </div>
                </CardFooter>
              </Card>
            </Col>
          ) : (
            ""
          )}
        </Row>
        <Row>
          <Col>
            <Card>
              <CardBody style={{ paddingLeft: "15%", paddingRight: "15%" }}>
                {" "}
                <h6 className="big-title">GAM Status</h6>
                <Pie
                  ref={deviceStatusRef}
                  data={{
                    labels: ["Online", "Offline"],
                    datasets: [
                      {
                        data: [
                          data.deviceStatusDisplay
                            ? data.deviceStatusDisplay.onlineCount
                            : "",
                          data.deviceStatusDisplay
                            ? data.deviceStatusDisplay.offlineCount
                            : "",
                        ],
                        backgroundColor: gamStatusColors,
                        borderColor: gamStatusColors,

                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={chartOptions}
                  onClick={clickDeviceStatus}
                />
              </CardBody>
              <CardFooter
                style={{
                  height: "40px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "9px",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  Online:&nbsp;
                  {data.deviceStatusDisplay
                    ? data.deviceStatusDisplay.onlineCount
                    : "0"}{" "}
                  - Offline:&nbsp;
                  {data.deviceStatusDisplay
                    ? data.deviceStatusDisplay.offlineCount
                    : "0"}
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card>
              <CardBody style={{ paddingLeft: "15%", paddingRight: "15%" }}>
                {" "}
                <h6 className="big-title">Active Alarms</h6>
                <Pie
                  ref={activeAlarmsRef}
                  data={{
                    labels: alarmsLabels,
                    datasets: [
                      {
                        data: alarmsCount,
                        backgroundColor: activeAlarmsColor,
                        borderColor: activeAlarmsColor,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={chartOptions}
                  onClick={clickActiveAlarms}
                />
              </CardBody>
              <CardFooter
                style={{
                  height: "40px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "9px",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  CR:&nbsp;{alarmsCount[0]} - MJ:&nbsp;{alarmsCount[1]} -
                  MN:&nbsp;{alarmsCount[2]} - NA:&nbsp;{alarmsCount[3]}
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card>
              {" "}
              <CardBody style={{ paddingLeft: "15%", paddingRight: "15%" }}>
                {" "}
                <h6 className="big-title">Endpoints Statuses</h6>
                <Pie
                  ref={quarantinedRef}
                  data={{
                    labels: [
                      "Offline",
                      "MJ TCA",
                      "MN TCA",
                      "Not Configured",
                      "OK",
                    ],
                    datasets: [
                      {
                        data: [
                          data.endpointsStatus
                            ? data.endpointsStatus.offlineCount
                            : "",
                          data.endpointsStatus
                            ? data.endpointsStatus.mjTcaCount
                            : "",
                          data.endpointsStatus
                            ? data.endpointsStatus.mnTcaCount
                            : "",
                          data.endpointsStatus
                            ? data.endpointsStatus.quarantinedCount
                            : "",
                          data.endpointsStatus
                            ? data.endpointsStatus.nonQuarantinedCount
                            : "",
                        ],
                        backgroundColor: endpointStatusColors,
                        borderColor: endpointStatusColors,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={chartOptions}
                  onClick={clickQuarantined}
                />
              </CardBody>
              <CardFooter
                style={{
                  height: "40px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "9px",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  Offline:&nbsp;
                  {data.endpointsStatus
                    ? data.endpointsStatus.offlineCount
                    : "0"}{" "}
                  - MJ TCA:&nbsp;
                  {data.endpointsStatus
                    ? data.endpointsStatus.mjTcaCount
                    : "0"}{" "}
                  - MN TCA:&nbsp;
                  {data.endpointsStatus
                    ? data.endpointsStatus.mnTcaCount
                    : "0"}{" "}
                  - Not Configured:&nbsp;
                  {data.endpointsStatus
                    ? data.endpointsStatus.quarantinedCount
                    : "0"}{" "}
                  - OK:&nbsp;
                  {data.endpointsStatus
                    ? data.endpointsStatus.nonQuarantinedCount
                    : "0"}{" "}
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col>
            <Card>
              {" "}
              <CardBody style={{ paddingLeft: "15%", paddingRight: "15%" }}>
                {" "}
                <h6 className="big-title">GAM Baseline</h6>
                <Pie
                  data={{
                    labels: ["Baseline", "Non-Baseline"],
                    datasets: [
                      {
                        data: [
                          data.baselinesDisplay
                            ? data.baselinesDisplay.baseline
                            : "",
                          data.baselinesDisplay
                            ? data.baselinesDisplay.nonBaseline
                            : "",
                        ],
                        backgroundColor: gamBaselineColors,
                        borderColor: gamBaselineColors,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              </CardBody>
              <CardFooter
                style={{
                  height: "40px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "9px",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  Baseline:&nbsp;
                  {data.baselinesDisplay
                    ? data.baselinesDisplay.baseline
                    : "0"}{" "}
                  - Non-Baseline:&nbsp;
                  {data.baselinesDisplay
                    ? data.baselinesDisplay.nonBaseline
                    : "0"}
                </div>
              </CardFooter>
            </Card>
          </Col>
          {data.installationDisplay ? (
            <Col>
              <Card>
                {" "}
                <CardBody style={{ paddingLeft: "15%", paddingRight: "15%" }}>
                  <h6 className="big-title">Installation Progress</h6>
                  <Pie
                    data={{
                      labels: [
                        "Done",
                        "Attention",
                        "Waiting",
                        "Unseen",
                        "Expected",
                      ],
                      datasets: [
                        {
                          data: [
                            data.installationDone,
                            data.installationAttention,
                            data.installationWaiting,
                            data.installationTodo,
                            data.expectedRooms,
                          ],
                          backgroundColor: installationColors,
                          borderColor: installationColors,
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={chartOptions}
                  />
                </CardBody>
                <CardFooter
                  style={{
                    height: "40px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "9px",
                      fontWeight: "bold",
                      width: "100%",
                    }}
                  >
                    Done:&nbsp;
                    {data.installationDone} - Attention:&nbsp;
                    {data.installationAttention} - Waiting:&nbsp;
                    {data.installationWaiting} - Unseen:&nbsp;
                    {data.installationTodo} - Expected:&nbsp;
                    {data.expectedRooms}
                  </div>
                </CardFooter>
              </Card>
            </Col>
          ) : (
            ""
          )}
        </Row>
        <Row>
          <Col sm="6">
            <Card>
              <CardBody>
                <h6 className="big-title">Subscribers per bandwidth profile</h6>
                <Bar
                  data={{
                    labels: subscriberPerBandwidthLabels,
                    datasets: [
                      {
                        label: "",
                        data: subscriberPerBandwidthCount,
                        backgroundColor: genericColors,
                        borderColor: genericColors,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                      title: {
                        display: false,
                      },
                    },
                    animation: {
                      duration: 0,
                    },
                  }}
                  height={100}
                />
              </CardBody>
            </Card>
          </Col>

          <Col sm="6">
            <Card>
              <CardBody>
                <h6 className="big-title">Software Versions</h6>
                <Bar
                  data={{
                    labels: softwareLabels,
                    datasets: [
                      {
                        label: "",
                        data: softwareCount,
                        backgroundColor: genericColors,
                        borderColor: genericColors,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                      title: {
                        display: false,
                      },
                    },
                    animation: {
                      duration: 0,
                    },
                  }}
                  height={100}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Dashboard;
